<template>
  <div class="swap-item">
    <router-link
      class="swap-item__link"
      :to="{ name: 'bytesforslag-detail', params: { id: suggestion.to_ad_id } }"
    >
      <div class="swap-item__photo">
        <img v-if="photoUrl" :src="photoUrl" :alt="movingTo.street">
      </div>

      <div class="swap-item__body">
        <div class="swap-item__address">
          <h3>{{ movingTo.street }} {{ movingTo.street_number }}</h3>
          <p class="swap-item__hood">
            <span v-if="movingTo.hood">{{ movingTo.hood }}, </span>{{ movingTo.city }}
          </p>
        </div>

        <ul class="swap-item__stats">
          <li v-if="movingTo.rooms != null">{{ movingTo.rooms }} {{ $t('ad.rooms') }}</li>
          <li v-if="movingTo.size != null">{{ movingTo.size }} m²</li>
          <li v-if="movingTo.rent != null">{{ movingTo.rent }} kr/{{ $t('ad.month') }}</li>
        </ul>

        <div class="swap-item__sizes">
          <span v-for="s in suggestion.swap_sizes" :key="s" class="swap-item__size-badge">
            {{ sizeLabel(s) }}
          </span>
        </div>

        <div v-if="chainStats" class="swap-item__chain">
          <div class="swap-item__progress">
            <div
              class="swap-item__progress-bar"
              :style="{ width: chainStats.best_progress_pct + '%' }"
            />
          </div>
          <p class="swap-item__chain-info">
            {{ chainStats.best_progress_pct }}% · {{ chainStats.total_live_chains }} {{ $t('swaps.chains') }}
          </p>
        </div>
      </div>
    </router-link>

    <div class="swap-item__actions">
      <button
        type="button"
        class="swap-item__btn swap-item__btn--no"
        :class="{ 'swap-item__btn--active': currentInterest === 0 }"
        :disabled="marking"
        :aria-pressed="currentInterest === 0"
        @click="mark(false)"
      >
        {{ $t('swaps.not-interested') }}
      </button>
      <button
        type="button"
        class="swap-item__btn swap-item__btn--yes"
        :class="{ 'swap-item__btn--active': currentInterest === 1 }"
        :disabled="marking"
        :aria-pressed="currentInterest === 1"
        @click="mark(true)"
      >
        {{ $t('swaps.interested') }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { v2PhotoUrl } from '@/utils/v2Photo'
import { swapSizeLabel, bucketToInterest } from '@/utils/swapMeta'

export default {
  name: 'SwapSuggestionItem',
  props: {
    suggestion: {
      type: Object,
      required: true
    }
  },
  computed: {
    ...mapGetters('swaps', ['isMarkingInterest']),
    movingTo () {
      return this.suggestion.moving_to || {}
    },
    chainStats () {
      return this.suggestion.chain_stats || null
    },
    photoUrl () {
      return v2PhotoUrl(this.movingTo.photo)
    },
    marking () {
      return this.isMarkingInterest(this.suggestion.to_ad_id)
    },
    currentInterest () {
      if (this.suggestion._userInterest === 0 || this.suggestion._userInterest === 1) {
        return this.suggestion._userInterest
      }
      return bucketToInterest(this.suggestion.bucket)
    }
  },
  methods: {
    sizeLabel (size) {
      return swapSizeLabel(size)
    },
    mark (interested) {
      if (this.marking) return
      const nextInterest = interested ? 1 : 0
      if (this.currentInterest === nextInterest) return
      this.$store.dispatch('swaps/markInterest', {
        toAdId: this.suggestion.to_ad_id,
        interested
      })
    }
  }
}
</script>

<style scoped>
.swap-item {
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: box-shadow 0.15s ease;
}

.swap-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.swap-item__link {
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 16px;
  padding: 12px;
  color: inherit;
  text-decoration: none;
}

.swap-item__photo {
  width: 140px;
  height: 100px;
  overflow: hidden;
  border-radius: 4px;
  background: #eee;
}

.swap-item__photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.swap-item__body {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.swap-item__address h3 {
  margin: 0;
  font-size: 1.05rem;
  line-height: 1.2;
}

.swap-item__hood {
  margin: 2px 0 0;
  font-size: 0.85rem;
  color: #666;
}

.swap-item__stats {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 0.85rem;
  color: #333;
}

.swap-item__sizes {
  display: flex;
  gap: 6px;
}

.swap-item__size-badge {
  display: inline-block;
  padding: 2px 8px;
  background: #e8f5f2;
  color: #2a7a6d;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 600;
}

.swap-item__chain {
  margin-top: 4px;
}

.swap-item__progress {
  height: 6px;
  background: #eee;
  border-radius: 3px;
  overflow: hidden;
}

.swap-item__progress-bar {
  height: 100%;
  background: #64bcb2;
  transition: width 0.2s ease;
}

.swap-item__chain-info {
  margin: 4px 0 0;
  font-size: 0.75rem;
  color: #666;
}

.swap-item__actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-top: 1px solid #eee;
}

.swap-item__btn {
  padding: 10px 12px;
  border: none;
  background: transparent;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.swap-item__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.swap-item__btn--no {
  color: #8a1a1a;
  border-right: 1px solid #eee;
}

.swap-item__btn--no:hover:not(:disabled) {
  background: #fae3e3;
}

.swap-item__btn--no.swap-item__btn--active {
    background-color: var(--color-no);

  /* background: #c04040; */
  color: #fff;
  cursor: default;
}

.swap-item__btn--no.swap-item__btn--active:hover:not(:disabled) {
  /* background: #c04040; */
    background-color: var(--color-no);

}

.swap-item__btn--yes {
  color: #1c6a2b;
}

.swap-item__btn--yes:hover:not(:disabled) {
  background: #e3f6e6;
}

.swap-item__btn--yes.swap-item__btn--active {
  /* background: #13b713; */
  background-color: var(--color-yes);
  color: #fff;
  cursor: default;
}

.swap-item__btn--yes.swap-item__btn--active:hover:not(:disabled) {
  /* background: #13b713; */
    background-color: var(--color-yes);
}

@media (max-width: 480px) {
  .swap-item__link {
    grid-template-columns: 100px 1fr;
  }
  .swap-item__photo {
    width: 100px;
    height: 75px;
  }
}
</style>
